import * as React from 'react';
import { css } from '@emotion/react';
import { TextField } from '../TextField/TextField';
import { MinusIcon, PlusIcon } from '../../icons';
import { FixMeLater } from '../../utils';
import { Button, ButtonProps } from '../Button/Button';
import { Stacker } from '../Stacker/Stacker';

export enum NumberPickerAction {
    INCREASE = 'increase',
    DECREASE = 'decrease',
}

export type NumberPickerTheme = {
    inputWidth: number;
};

export const numberPickerTheme: NumberPickerTheme = {
    inputWidth: 48,
};
export interface NumberPickerProps {
    /**
     * The id of the input element.
     */
    id: string;

    /**
     * If the input should be of type read-only.
     */
    inputReadOnly?: boolean;

    /**
     * The maximum permitted value.
     * @default 99
     */
    max?: number;

    /**
     * The minimum permitted value.
     * @default 1
     */
    min?: number;

    /**
     * The name attribute of the input element.
     */
    name?: string;

    /**
     * Callback fired when the input loses focus.
     */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * Callback fired when the value of the input is changed.
     */
    onChange?: (value: number) => void;

    /**
     * The increment by which the value will be increased or decreased when using the button controls.
     * @default 1
     */
    step?: number;

    /**
     * The value of the input element.
     * @default 1
     */
    value?: number;

    /**
     * The value of the loading state.
     * @default false
     */
    loadingState?: boolean;
    /**
     * Additional properties applied to the button components.
     * @default {shape: 'round', variant: 'hollow', size: 'l' }
     */
    ButtonProps?: ButtonProps;
}

/**
 * NumberPicker allows us to enter and toggle between different number values by entering or selecting through the stepper buttons.
 * The component allows us to control the step value for the picker, define a range of numbers to select from using 'max' and 'min' props.
 * This allows us to customise the picker to behave and act differently based on different need and requirement. Furthermore, we can decide whether to
 * allow entering via input field or just use stepper by making the input field read only, and it also supports the loading behaviour out of the box which
 * disables the picker if loadingState prop is enabled.
 */
export const NumberPicker: React.ForwardRefExoticComponent<NumberPickerProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function NumberPicker(
        props: NumberPickerProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const {
            id,
            inputReadOnly,
            max = 99,
            min = 1,
            name,
            onBlur,
            onChange,
            step = 1,
            value = 1,
            loadingState = false,
            ButtonProps: ButtonPropsProp,
            ...other
        } = props;

        const adjustNumber = React.useCallback(
            (action: NumberPickerAction, val: number) => {
                let number = val + (action === NumberPickerAction.INCREASE ? step : step * -1);
                number = Math.max(Math.min(number, max), min);

                if (onChange) {
                    onChange(number);
                }
            },
            [max, min, onChange, step]
        );

        const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
            switch (event.key) {
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'ArrowUp':
                case 'ArrowDown':
                case 'Backspace':
                case 'Delete':
                case 'Enter':
                case 'Tab':
                case '1':
                case '2':
                case '3':
                case '4':
                case '5':
                case '6':
                case '7':
                case '8':
                case '9':
                case '0':
                    // These are permitted keys
                    break;
                default:
                    // The key must be a number
                    event.preventDefault();
            }
        }, []);

        const handleChange = React.useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                const { value: val } = event.target;
                if (onChange) {
                    onChange(parseInt(val));
                }
            },
            [onChange]
        );

        const handleBlur = React.useCallback(
            (event: React.FocusEvent<HTMLInputElement>, val: number) => {
                let number = val;

                const isValid = (): boolean => {
                    if (Number.isNaN(number) || number < min) {
                        number = min;
                        return false;
                    }
                    if (number > max) {
                        number = max;
                        return false;
                    }
                    return true;
                };

                if (!isValid() && onChange) {
                    onChange(number);
                }

                if (onBlur) {
                    onBlur(event);
                }
            },
            [max, min, onBlur, onChange]
        );

        const inputStyles = css`
            width: ${numberPickerTheme.inputWidth}px;

            &[type='number']::-webkit-outer-spin-button,
            &[type='number']::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
            &[type='number'] {
                -moz-appearance: textfield;
            }
        `;

        const buttonProps = {
            shape: 'round',
            variant: 'outlined',
            size: 'l',
            ...ButtonPropsProp,
        } as ButtonProps;

        return (
            <Stacker ref={ref} direction="row" spacing={1} alignItems="center" {...other}>
                <Button
                    icon={<MinusIcon size="s" />}
                    onClick={() => adjustNumber(NumberPickerAction.DECREASE, value)}
                    disabled={value - 1 < min || loadingState}
                    aria-label="Decrease"
                    aria-controls={id}
                    data-id="number-picker-decrease"
                    {...buttonProps}
                />

                <TextField
                    id={id}
                    name={name}
                    css={inputStyles}
                    type="number"
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e, value)}
                    onKeyDown={handleKeyDown}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                    aria-live="assertive"
                    value={value.toString()}
                    min={min}
                    max={max}
                    readOnly={inputReadOnly}
                    textAlign="center"
                    data-id="number-picker-count"
                    size="s"
                    variant="subtle"
                />

                <Button
                    icon={<PlusIcon />}
                    onClick={() => adjustNumber(NumberPickerAction.INCREASE, value)}
                    disabled={value + 1 > max || loadingState}
                    aria-label="Increase"
                    aria-controls={id}
                    data-id="number-picker-increase"
                    {...buttonProps}
                />
            </Stacker>
        );
    });
